import { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faRoute,
  faMapMarkerAlt,
  faDirections,
  faShareAlt,
} from '@fortawesome/pro-regular-svg-icons';

import eventBus from '../../../eventBus';
import { interactiveURL } from '../../../config';

import {
  useAppDispatch,
  useAppSelector,
  routeEndpointsSelector,
  customRouteStartSelector,
  customRouteEndSelector,
  setCustomRouteStart,
  setCustomRouteEnd,
  setShowCustomRoute,
  clearCustomRoute,
  customRouteResultSelector,
  showCustomRouteSelector,
  currentLayerEdgesSelector,
  mapSelector,
  activeImageSelector,
  customRouteFromWaypointSelector,
  preferencesSelector,
} from '../../../store';

import './IndoorRouteSidebar.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

/**
 * Sidebar for selecting indoor route start/end points.
 * Uses the custom pathfinding algorithm instead of MapBox.
 */
export function IndoorRouteSidebar({ isOpen, onClose, onOpen }: Props) {
  const dispatch = useAppDispatch();
  const [manualCopyUrl, setManualCopyUrl] = useState<string | null>(null);

  const availableSpaces = useAppSelector(routeEndpointsSelector);
  const startSpaceId = useAppSelector(customRouteStartSelector);
  const endSpaceId = useAppSelector(customRouteEndSelector);
  const routeResult = useAppSelector(customRouteResultSelector);
  const showRoute = useAppSelector(showCustomRouteSelector);
  const edges = useAppSelector(currentLayerEdgesSelector);

  const iMap = useAppSelector(mapSelector);
  const activeImage = useAppSelector(activeImageSelector);
  const preferences = useAppSelector(preferencesSelector);
  const showWayfindingButton = preferences.wayfindingButton ?? true;
  const fromWaypointId = useAppSelector(customRouteFromWaypointSelector);
  const isWaypointRoute = !!fromWaypointId;

  const hasPathNetwork = edges.length > 0;

  const copyRouteLink = useCallback(async () => {
    const url = new URL(
      `${interactiveURL}/${iMap.order.id}/map/${iMap.id}/layer/${activeImage?.id}`
    );
    if (isWaypointRoute && fromWaypointId) {
      url.searchParams.delete('routeFrom');
      url.searchParams.delete('routeTo');
      url.searchParams.set('from_waypoint', fromWaypointId);
    } else {
      const startSpace = availableSpaces.find((s) => s.id === startSpaceId);
      const endSpace = availableSpaces.find((s) => s.id === endSpaceId);
      const toSlug = (id: string, name?: string) =>
        name ? `${id}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}` : id;
      url.searchParams.set('routeFrom', toSlug(startSpaceId || '', startSpace?.name));
      url.searchParams.set('routeTo', toSlug(endSpaceId || '', endSpace?.name));
    }

    const routeUrl = url.toString();

    try {
      await navigator.clipboard.writeText(routeUrl);
      setManualCopyUrl(null);
      eventBus.emit('flash', 'success', 'Route link copied to clipboard!');
      return;
    } catch {
      // Some embedded contexts block Clipboard API without explicit iframe permissions.
    }

    try {
      const textArea = document.createElement('textarea');
      textArea.value = routeUrl;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.top = '-9999px';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length);

      const copied = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (copied) {
        setManualCopyUrl(null);
        eventBus.emit('flash', 'success', 'Route link copied to clipboard!');
        return;
      }
    } catch {
      // No-op: final fallback below.
    }

    setManualCopyUrl(routeUrl);
    eventBus.emit('flash', 'error', 'Unable to auto-copy in this embedded view. Use the manual copy field below Share.');
  }, [iMap, activeImage, isWaypointRoute, fromWaypointId, startSpaceId, endSpaceId, availableSpaces]);

  const handleStartChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value || null;
    dispatch(setCustomRouteStart(value));
    if (value && endSpaceId) {
      dispatch(setShowCustomRoute(true));
    }
  }, [dispatch, endSpaceId]);

  const handleEndChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value || null;
    dispatch(setCustomRouteEnd(value));
    if (startSpaceId && value) {
      dispatch(setShowCustomRoute(true));
    }
  }, [dispatch, startSpaceId]);

  const handleClearRoute = useCallback(() => {
    dispatch(clearCustomRoute());
  }, [dispatch]);

  const handleSwapPoints = useCallback(() => {
    const temp = startSpaceId;
    dispatch(setCustomRouteStart(endSpaceId));
    dispatch(setCustomRouteEnd(temp));
  }, [dispatch, startSpaceId, endSpaceId]);

  if (!showWayfindingButton) return null;

  // Toggle button when sidebar is closed
  if (!isOpen) {
    return (
      <button
        className="indoor-route-toggle-btn"
        onClick={onOpen}
        title="Open Wayfinding"
        aria-label="Open Wayfinding"
      >
        <FontAwesomeIcon icon={faDirections} />
      </button>
    );
  }

  return (
    <aside className="indoor-route-sidebar" aria-label="Wayfinding">
      <header className="sidebar-header">
        <h2>
          <FontAwesomeIcon icon={faRoute} aria-hidden="true" />
          Wayfinding
        </h2>
        <button
          className="close-btn"
          onClick={onClose}
          title="Close Wayfinding"
        >
          <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
        </button>
      </header>

      <div className="sidebar-content">
        {!hasPathNetwork ? (
          <div className="no-paths-message">
            <p>No navigation paths have been set up for this map yet.</p>
            <p className="hint">Paths can be drawn by editors using the path drawing tool.</p>
          </div>
        ) : (
          <>
            <section className="route-selection">
              <h3>Find your way</h3>

              <div className="location-input">
                <label htmlFor="start-location">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="start-icon" />
                  Start
                </label>
                <select
                  id="start-location"
                  value={startSpaceId || ''}
                  onChange={handleStartChange}
                >
                  <option value="">Select starting point...</option>
                  {availableSpaces.map((space) => (
                    <option key={space.id} value={space.id} disabled={space.id === endSpaceId}>
                      {space.name}
                    </option>
                  ))}
                </select>
              </div>

              {startSpaceId && endSpaceId && (
                <button
                  className="swap-btn"
                  onClick={handleSwapPoints}
                  title="Swap start and end"
                >
                  ⇅
                </button>
              )}

              <div className="location-input">
                <label htmlFor="end-location">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="end-icon" />
                  End
                </label>
                <select
                  id="end-location"
                  value={endSpaceId || ''}
                  onChange={handleEndChange}
                >
                  <option value="">Select destination...</option>
                  {availableSpaces.map((space) => (
                    <option key={space.id} value={space.id} disabled={space.id === startSpaceId}>
                      {space.name}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            {/* Route Result */}
            {startSpaceId && endSpaceId && routeResult && (
              <section className="route-result">
                {routeResult.found ? (
                  <>
                    <h3>Route Found</h3>

                    {showRoute && (
                      <div className="route-actions">
                        <button className="share-btn" onClick={copyRouteLink}>
                          <FontAwesomeIcon icon={faShareAlt} /> Share
                        </button>
                        <button className="clear-btn" onClick={handleClearRoute}>
                          Clear Route
                        </button>
                        {manualCopyUrl && (
                          <div className="manual-copy-fallback">
                            <label htmlFor="manual-copy-route-url">Copy this link manually</label>
                            <input
                              id="manual-copy-route-url"
                              type="text"
                              readOnly
                              value={manualCopyUrl}
                              onFocus={(e) => e.currentTarget.select()}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-route-found">
                    <p>No path found between these locations.</p>
                    <p className="hint">The locations may not be connected by navigation paths.</p>
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
